import { useQuery } from '@tanstack/react-query';

export interface DailyQuote {
  text: string;
  author: string;
}

const FALLBACK_QUOTES: DailyQuote[] = [
  { text: 'The secret of getting ahead is getting started.', author: 'Mark Twain' },
  { text: 'Small daily improvements are the key to staggering long-term results.', author: 'Unknown' },
  { text: 'Focus on being productive instead of busy.', author: 'Tim Ferriss' },
  { text: 'You do not have to be great to start, but you have to start to be great.', author: 'Zig Ziglar' },
  { text: 'The way to get started is to quit talking and begin doing.', author: 'Walt Disney' },
  { text: 'It always seems impossible until it is done.', author: 'Nelson Mandela' },
  { text: 'Done is better than perfect.', author: 'Sheryl Sandberg' },
  { text: 'Start where you are. Use what you have. Do what you can.', author: 'Arthur Ashe' },
];

function pickFallback(): DailyQuote {
  const day = new Date().getDate();
  return FALLBACK_QUOTES[day % FALLBACK_QUOTES.length]!;
}

async function fetchDailyQuote(): Promise<DailyQuote> {
  try {
    const res = await fetch(
      'https://api.quotable.io/random?maxLength=140&tags=motivational|inspirational|wisdom|success',
      { signal: AbortSignal.timeout(4000) },
    );
    if (res.ok) {
      const data = (await res.json()) as { content: string; author: string };
      if (data.content?.trim()) {
        return { text: data.content.trim(), author: data.author?.trim() || 'Unknown' };
      }
    }
  } catch {
    // Offline or API unavailable, fall through to local quotes.
  }
  return pickFallback();
}

export function useDailyQuote() {
  return useQuery({
    queryKey: ['daily-quote'],
    queryFn: fetchDailyQuote,
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 24,
    retry: false,
    placeholderData: pickFallback,
  });
}
